// src/components/schoolmate/SchoolmateManager.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { 
  School, Calendar, Users, Box, Grid3X3, Plus, Search, Trash2, Edit3, X, Loader2, 
  CheckCircle, Clock, GraduationCap, ClipboardList, Target, ChevronRight, 
  UserPlus, Layers, Save, AlertCircle
} from 'lucide-react';
import { schoolmateService, LogType, ActivityType, ActivityTypeMeta, ActivityTypeOrder, ActivityStatusMeta } from '../../services/schoolmateService';

const ACTOR_TYPES = {
  learner: { label: 'Learner', icon: GraduationCap, color: 'blue' },
  staff: { label: 'Staff', icon: School, color: 'indigo' },
  sgb: { label: 'SGB', icon: Users, color: 'amber' },
  stakeholder: { label: 'Stakeholder', icon: Users, color: 'purple' }
};

const STAFF_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'educator', label: 'Educator' },
  { value: 'support', label: 'Support' }
];

const ACTIVITY_TYPE_OPTIONS = ActivityTypeOrder.map((id) => ({
  id,
  label: ActivityTypeMeta[id]?.label || id
}));

const ITEM_TYPES = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'movable', label: 'Movable' }
];

const ITEM_OWNERSHIPS = [
  { value: 'owned', label: 'Owned' },
  { value: 'leased', label: 'Leased' },
  { value: 'rented', label: 'Rented' },
  { value: 'borrowed', label: 'Borrowed' }
];

const PLAN_TYPES = [
  { value: 'improvement', label: 'School Improvement Plan (SIP)' },
  { value: 'operational', label: 'Operational Plan' },
  { value: 'teaching', label: 'Annual Teaching Plan (ATP)' },
  { value: 'assessment', label: 'Programme of Assessment (PoA)' },
  { value: 'departmental', label: 'Departmental Improvement Plan' },
  { value: 'subject', label: 'Subject Improvement Plan (SIP)' },
  { value: 'lesson', label: 'Lesson Plan' },
  { value: 'other', label: 'Other' }
];

const SYSTEM_TYPES = [
  { value: 'vision', label: 'Vision' },
  { value: 'mission', label: 'Mission' },
  { value: 'values', label: 'Values' },
  { value: 'goals', label: 'Goals' },
  { value: 'targets', label: 'Targets' },
  { value: 'strategy', label: 'Strategy' },
  { value: 'organogram', label: 'Organogram' },
  { value: 'qms', label: 'QMS' },
  { value: 'cptd', label: 'CPTD' },
  { value: 'policies', label: 'Policies' },
  { value: 'processes', label: 'Processes' },
  { value: 'procedures', label: 'Procedures' }
];

const DEFAULT_ITEM_CATEGORIES = [
  'LTSM',
  'Equipment - Office',
  'Equipment - Classroom',
  'Equipment - Sports',
  'Equipment - Lab',
  'Furniture',
  'Sports'
];

const SGB_ROLES = [
  { value: 'chair', label: 'Chair' },
  { value: 'secretary', label: 'Secretary' },
  { value: 'treasurer', label: 'Treasurer' },
  { value: 'member', label: 'Member' }
];

const STAKEHOLDER_ROLES = [
  { value: 'parent', label: 'Parent' },
  { value: 'guardian', label: 'Guardian' },
  { value: 'sponsor', label: 'Sponsor' },
  { value: 'community', label: 'Community' },
  { value: 'other', label: 'Other' }
];

const normalizeActorType = (type) => {
  if (type === 'student') return 'learner';
  if (type === 'teacher') return 'staff';
  return type || 'staff';
};

const getRoleLabel = (roleList, value) => roleList.find(r => r.value === value)?.label || '';

const DAYS = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' }
];

const NAV_ITEMS = [
  { id: 'overview', label: 'Overview', icon: School },
  { id: 'actors', label: 'Actors', icon: Users },
  { id: 'items', label: 'Items', icon: Box },
  { id: 'activities', label: 'Activities', icon: Calendar },
  { id: 'timetable', label: 'Timetable', icon: Grid3X3 },
  { id: 'planner', label: 'Planner', icon: Target },
  { id: 'marklists', label: 'Marklists', icon: ClipboardList },
  { id: 'systems', label: 'Systems', icon: Layers }
];

export const SchoolmateManager = ({ bookId, book, onClose }) => {
  const [view, setView] = useState('overview');
  const [itemsTab, setItemsTab] = useState('classes');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [search, setSearch] = useState('');
  
  // Data
  const [stats, setStats] = useState(null);
  const [actors, setActors] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [items, setItems] = useState([]);
  const [slots, setSlots] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [plans, setPlans] = useState([]);
  const [systems, setSystems] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [planActions, setPlanActions] = useState([]);
  const [selectedSystemId, setSelectedSystemId] = useState('');
  const [systemSubsystems, setSystemSubsystems] = useState([]);

  const itemCategories = useMemo(() => {
    const categories = new Set(DEFAULT_ITEM_CATEGORIES);
    items.forEach(item => {
      if (item.category) categories.add(item.category);
    });
    return Array.from(categories);
  }, [items]);

  const actorCounts = useMemo(() => {
    const counts = { learner: 0, staff: 0, sgb: 0, stakeholder: 0 };
    actors.forEach((actor) => {
      const type = normalizeActorType(actor.type);
      if (counts[type] !== undefined) counts[type] += 1;
    });
    return counts;
  }, [actors]);
  
  // Modal
  const [modal, setModal] = useState({ open: false, type: null, item: null });

  useEffect(() => { loadData(); }, [bookId]);
  useEffect(() => {
    if (!selectedPlanId && plans.length > 0) {
      setSelectedPlanId(plans[0].id);
    }
  }, [plans, selectedPlanId]);
  useEffect(() => {
    loadPlanActions();
  }, [selectedPlanId]);
  useEffect(() => {
    if (!selectedSystemId && systems.length > 0) {
      setSelectedSystemId(systems[0].id);
    }
  }, [systems, selectedSystemId]);
  useEffect(() => {
    loadSystemSubsystems();
  }, [selectedSystemId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsData, actorsData, classesData, subjectsData, itemsData, slotsData, assessmentsData, activitiesData, plansData, systemsData] = await Promise.all([
        schoolmateService.getStats(bookId),
        schoolmateService.getActors(bookId),
        schoolmateService.getClasses(bookId),
        schoolmateService.getSubjects(bookId),
        schoolmateService.getItems(bookId),
        schoolmateService.getScheduleSlots(bookId),
        schoolmateService.getAssessments(bookId),
        schoolmateService.getActivities(bookId),
        schoolmateService.getPlannerPlans(bookId),
        schoolmateService.getSystems(bookId)
      ]);
      setStats(statsData);
      setActors(actorsData);
      setClasses(classesData);
      setSubjects(subjectsData);
      setItems(itemsData);
      setSlots(slotsData);
      setAssessments(assessmentsData);
      setActivities(activitiesData);
      setPlans(plansData);
      setSystems(systemsData);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const loadPlanActions = async () => {
    if (!selectedPlanId) {
      setPlanActions([]);
      return;
    }
    try {
      const actionsData = await schoolmateService.getPlannerActions(bookId, selectedPlanId);
      setPlanActions(actionsData);
    } catch (err) {
      console.error(err);
    }
  };

  const loadSystemSubsystems = async () => {
    if (!selectedSystemId) {
      setSystemSubsystems([]);
      return;
    }
    try {
      const subsystemsData = await schoolmateService.getSystemSubsystems(bookId, selectedSystemId);
      setSystemSubsystems(subsystemsData);
    } catch (err) {
      console.error(err);
    }
  };

  const notify = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const openModal = (type, item = null) => setModal({ open: true, type, item });
  const closeModal = () => setModal({ open: false, type: null, item: null });

  // CRUD handlers
  const handleSaveActor = async (data) => {
    setSaving(true);
    try {
      await schoolmateService.saveActor(bookId, data);
      notify(data.id ? 'Person updated' : 'Person added');
      loadData();
      closeModal();
    } catch (err) {
      notify('Failed to save', 'error');
    }
    setSaving(false);
  };

  const handleDeleteActor = async (id) => {
    if (!window.confirm('Delete this person?')) return;
    try {
      await schoolmateService.deleteActor(bookId, id);
      notify('Person deleted');
      loadData();
    } catch (err) {
      notify('Failed to delete', 'error');
    }
  };

  const handleSaveClass = async (data) => {
    setSaving(true);
    try {
      await schoolmateService.saveClass(bookId, data);
      notify(data.id ? 'Class updated' : 'Class added');
      loadData();
      closeModal();
    } catch (err) {
      notify('Failed to save', 'error');
    }
    setSaving(false);
  };

  const handleDeleteClass = async (id) => {
    if (!window.confirm('Delete this class?')) return;
    try {
      await schoolmateService.deleteClass(bookId, id);
      notify('Class deleted');
      loadData();
    } catch (err) {
      notify('Failed to delete', 'error');
    }
  };

  const handleSaveSubject = async (data) => {
    setSaving(true);
    try {
      await schoolmateService.saveSubject(bookId, data);
      notify(data.id ? 'Subject updated' : 'Subject added');
      loadData();
      closeModal();
    } catch (err) {
      notify('Failed to save', 'error');
    }
    setSaving(false);
  };

  const handleDeleteSubject = async (id) => {
    if (!window.confirm('Delete this subject?')) return;
    try {
      await schoolmateService.deleteSubject(bookId, id);
      notify('Subject deleted');
      loadData();
    } catch (err) {
      notify('Failed to delete', 'error');
    }
  };

  const handleSaveItem = async (data) => {
    setSaving(true);
    try {
      await schoolmateService.saveItem(bookId, data);
      notify(data.id ? 'Item updated' : 'Item added');
      loadData();
      closeModal();
    } catch (err) {
      notify('Failed to save', 'error');
    }
    setSaving(false);
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await schoolmateService.deleteItem(bookId, id);
      notify('Item deleted');
      loadData();
    } catch (err) {
      notify('Failed to delete', 'error');
    }
  };

  const handleSaveSlot = async (data) => {
    setSaving(true);
    try {
      await schoolmateService.saveScheduleSlot(bookId, data);
      notify(data.id ? 'Slot updated' : 'Slot added');
      loadData();
      closeModal();
    } catch (err) {
      notify('Failed to save', 'error');
    }
    setSaving(false);
  };

  const handleDeleteSlot = async (id) => {
    if (!window.confirm('Delete this slot?')) return;
    try {
      await schoolmateService.deleteScheduleSlot(bookId, id);
      notify('Slot deleted');
      loadData();
    } catch (err) {
      notify('Failed to delete', 'error');
    }
  };

  const handleSaveAssessment = async (data) => {
    setSaving(true);
    try {
      await schoolmateService.saveAssessment(bookId, data);
      notify(data.id ? 'Assessment updated' : 'Assessment created');
      loadData();
      closeModal();
    } catch (err) {
      notify('Failed to save', 'error');
    }
    setSaving(false);
  };

  const handleSaveTask = async (data, label = 'Activity') => {
    setSaving(true);
    try {
      await schoolmateService.saveActivity(bookId, data);
      notify(data.id ? `${label} updated` : `${label} created`);
      loadData();
      closeModal();
    } catch (err) {
      notify('Failed to save', 'error');
    }
    setSaving(false);
  };

  const handleSavePlan = async (data) => {
    setSaving(true);
    try {
      await schoolmateService.savePlannerPlan(bookId, data);
      notify(data.id ? 'Plan updated' : 'Plan created');
      loadData();
      closeModal();
    } catch (err) {
      notify('Failed to save', 'error');
    }
    setSaving(false);
  };

  const handleDeletePlan = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    try {
      await schoolmateService.deletePlannerPlan(bookId, id);
      notify('Plan deleted');
      loadData();
    } catch (err) {
      notify('Failed to delete', 'error');
    }
  };

  const handleSaveSystem = async (data) => {
    setSaving(true);
    try {
      await schoolmateService.saveSystem(bookId, data);
      notify(data.id ? 'System updated' : 'System created');
      loadData();
      closeModal();
    } catch (err) {
      notify('Failed to save', 'error');
    }
    setSaving(false);
  };

  const handleSaveSystemSubsystem = async (data) => {
    if (!data.systemId) {
      notify('Select a system first', 'error');
      return;
    }
    setSaving(true);
    try {
      await schoolmateService.saveSystemSubsystem(bookId, data.systemId, data);
      notify(data.id ? 'Subsystem updated' : 'Subsystem created');
      loadSystemSubsystems();
      closeModal();
    } catch (err) {
      notify('Failed to save', 'error');
    }
    setSaving(false);
  };

  const handleDeleteSystemSubsystem = async (systemId, subsystemId) => {
    if (!window.confirm('Delete this subsystem?')) return;
    try {
      await schoolmateService.deleteSystemSubsystem(bookId, systemId, subsystemId);
      notify('Subsystem deleted');
      loadSystemSubsystems();
    } catch (err) {
      notify('Failed to delete', 'error');
    }
  };

  const handleSavePlanAction = async (data) => {
    if (!data.planId) {
      notify('Select a plan first', 'error');
      return;
    }
    setSaving(true);
    try {
      await schoolmateService.savePlannerAction(bookId, data.planId, data);
      notify(data.id ? 'Action updated' : 'Action created');
      loadPlanActions();
      closeModal();
    } catch (err) {
      notify('Failed to save', 'error');
    }
    setSaving(false);
  };

  const handleDeletePlanAction = async (planId, actionId) => {
    if (!window.confirm('Delete this action?')) return;
    try {
      await schoolmateService.deletePlannerAction(bookId, planId, actionId);
      notify('Action deleted');
      loadPlanActions();
    } catch (err) {
      notify('Failed to delete', 'error');
    }
  };

  const handleGraduatePlanAction = async (planId, action) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) {
      notify('Plan not found', 'error');
      return;
    }
    try {
      await schoolmateService.graduatePlannerAction(bookId, plan, action);
      notify('Action graduated to activity');
      loadPlanActions();
      loadData();
    } catch (err) {
      notify('Failed to graduate', 'error');
    }
  };

  const handleDeleteSystem = async (id) => {
    if (!window.confirm('Delete this system?')) return;
    try {
      await schoolmateService.deleteSystem(bookId, id);
      notify('System deleted');
      loadData();
    } catch (err) {
      notify('Failed to delete', 'error');
    }
  };

  // Filter helper
  const filter = (items, keys) => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(i => keys.some(k => i[k]?.toLowerCase().includes(q)));
  };

  // Render views
  const renderItems = ({ hideHeader = false } = {}) => (
    <div className="space-y-4">
      {!hideHeader && (
        <div className="flex justify-end">
          <button onClick={() => openModal('item')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add Item
          </button>
        </div>
      )}
      <div className="bg-white rounded-xl border overflow-hidden">
        {items.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No items yet</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Item</th>
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Ownership</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Location</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-indigo-100 overflow-hidden flex items-center justify-center text-indigo-600 text-xs font-semibold">
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.name || 'Item'} className="h-full w-full object-cover" />
                        ) : (
                          (item.name || 'I').slice(0, 2).toUpperCase()
                        )}
                      </div>
                      <span>{item.name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">{item.category || '-'}</td>
                  <td className="px-3 py-2 capitalize">{item.itemType || '-'}</td>
                  <td className="px-3 py-2 capitalize">{item.ownership || '-'}</td>
                  <td className="px-3 py-2 capitalize">{item.status || '-'}</td>
                  <td className="px-3 py-2">{item.location || '-'}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openModal('item', item)} className="p-1 hover:bg-gray-100 rounded"><Edit3 className="h-4 w-4" /></button>
                    <button onClick={() => handleDeleteItem(item.id)} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-indigo-50 p-4 rounded-xl">
          <p className="text-2xl font-bold text-indigo-600">{actorCounts.staff}</p>
          <p className="text-sm text-indigo-700">Staff</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-xl">
          <p className="text-2xl font-bold text-blue-600">{actorCounts.learner}</p>
          <p className="text-sm text-blue-700">Learners</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-xl">
          <p className="text-2xl font-bold text-purple-600">{actorCounts.stakeholder}</p>
          <p className="text-sm text-purple-700">Stakeholders</p>
        </div>
        <div className="bg-emerald-50 p-4 rounded-xl">
          <p className="text-2xl font-bold text-emerald-600">{stats?.subjects || 0}</p>
          <p className="text-sm text-emerald-700">Subjects</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white p-4 rounded-xl border">
          <h3 className="font-semibold mb-3">Quick Actions</h3>
          <div className="space-y-2">
            <button onClick={() => openModal('actor')} className="w-full p-3 text-left rounded-lg hover:bg-gray-50 flex items-center gap-3">
              <UserPlus className="h-5 w-5 text-indigo-600" />
              <span>Add Person</span>
            </button>
            <button onClick={() => openModal('class')} className="w-full p-3 text-left rounded-lg hover:bg-gray-50 flex items-center gap-3">
              <Layers className="h-5 w-5 text-blue-600" />
              <span>Create Class</span>
            </button>
            <button onClick={() => openModal('slot')} className="w-full p-3 text-left rounded-lg hover:bg-gray-50 flex items-center gap-3">
              <Calendar className="h-5 w-5 text-purple-600" />
              <span>Add Schedule Slot</span>
            </button>
            <button onClick={() => openModal('assessment')} className="w-full p-3 text-left rounded-lg hover:bg-gray-50 flex items-center gap-3">
              <ClipboardList className="h-5 w-5 text-amber-600" />
              <span>Create Assessment</span>
            </button>
            <button onClick={() => openModal('activity')} className="w-full p-3 text-left rounded-lg hover:bg-gray-50 flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
              <span>Add Activity</span>
            </button>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border">
          <h3 className="font-semibold mb-3">Recent Activity</h3>
          {stats?.recentActivity?.length > 0 ? (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {stats.recentActivity.slice(0, 6).map(log => (
                <div key={log.id} className="text-sm p-2 bg-gray-50 rounded">
                  <span className="text-gray-600">{log.note || `${log.type} logged`}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No recent activity</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderActors = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="px-4 py-2 border rounded-lg w-64" />
        <button onClick={() => openModal('actor')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
          <Plus className="h-4 w-4" /> Add Person
        </button>
      </div>
      
      <div className="bg-white rounded-xl border overflow-hidden">
        {filter(actors, ['name', 'email']).length === 0 ? (
          <div className="p-8 text-center text-gray-500">No people found</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Email</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Class / Role</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filter(actors, ['name', 'email']).map(a => {
                const normalizedType = normalizeActorType(a.type);
                const type = ACTOR_TYPES[normalizedType] || ACTOR_TYPES.staff;
                const roleLabel = normalizedType === 'staff'
                  ? getRoleLabel(STAFF_ROLES, a.staffRole)
                  : normalizedType === 'sgb'
                    ? getRoleLabel(SGB_ROLES, a.sgbRole)
                    : normalizedType === 'stakeholder'
                      ? getRoleLabel(STAKEHOLDER_ROLES, a.stakeholderRole)
                      : '';
                return (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{a.name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs bg-${type.color}-100 text-${type.color}-700`}>
                        {type.label}{roleLabel ? ` • ${roleLabel}` : ''}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{a.email || '-'}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {normalizedType === 'learner'
                        ? (a.className || '-')
                        : roleLabel || a.position || '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openModal('actor', a)} className="p-1 hover:bg-gray-100 rounded"><Edit3 className="h-4 w-4" /></button>
                      <button onClick={() => handleDeleteActor(a.id)} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const renderClasses = ({ hideHeader = false } = {}) => (
    <div className="space-y-4">
      {!hideHeader && (
        <div className="flex justify-end">
          <button onClick={() => openModal('class')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add Class
          </button>
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-4">
        {classes.map(c => (
          <div key={c.id} className="bg-white p-4 rounded-xl border">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h4 className="font-semibold">{c.name}</h4>
                <p className="text-sm text-gray-500">Grade {c.grade}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openModal('class', c)} className="p-1 hover:bg-gray-100 rounded"><Edit3 className="h-4 w-4" /></button>
                <button onClick={() => handleDeleteClass(c.id)} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <p className="text-sm text-gray-500">{c.teacherName || 'No class teacher'}</p>
          </div>
        ))}
        {classes.length === 0 && <div className="col-span-3 text-center py-8 text-gray-500">No classes yet</div>}
      </div>
    </div>
  );

  const renderSubjects = ({ hideHeader = false } = {}) => (
    <div className="space-y-4">
      {!hideHeader && (
        <div className="flex justify-end">
          <button onClick={() => openModal('subject')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add Subject
          </button>
        </div>
      )}
      <div className="grid md:grid-cols-4 gap-3">
        {subjects.map(s => (
          <div key={s.id} className="bg-white p-4 rounded-xl border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color || '#6366f1' }} />
              <div>
                <p className="font-medium">{s.name}</p>
                <p className="text-xs text-gray-500">{s.code || ''}</p>
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => openModal('subject', s)} className="p-1 hover:bg-gray-100 rounded"><Edit3 className="h-4 w-4" /></button>
              <button onClick={() => handleDeleteSubject(s.id)} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {subjects.length === 0 && <div className="col-span-4 text-center py-8 text-gray-500">No subjects yet</div>}
      </div>
    </div>
  );

  const renderSlots = ({ hideHeader = false } = {}) => (
    <div className="space-y-4">
      {!hideHeader && (
        <div className="flex justify-end">
          <button onClick={() => openModal('slot')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add Slot
          </button>
        </div>
      )}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">Day</th>
              <th className="px-3 py-2 text-left">Period</th>
              <th className="px-3 py-2 text-left">Time</th>
              <th className="px-3 py-2 text-left">Class</th>
              <th className="px-3 py-2 text-left">Subject</th>
              <th className="px-3 py-2 text-left">Teacher</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {slots.map(s => (
              <tr key={s.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 capitalize">{s.day}</td>
                <td className="px-3 py-2">P{s.period}</td>
                <td className="px-3 py-2">{s.startTime}-{s.endTime}</td>
                <td className="px-3 py-2">{s.className}</td>
                <td className="px-3 py-2">{s.subjectName}</td>
                <td className="px-3 py-2">{s.teacherName}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => openModal('slot', s)} className="p-1 hover:bg-gray-100 rounded"><Edit3 className="h-4 w-4" /></button>
                  <button onClick={() => handleDeleteSlot(s.id)} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {slots.length === 0 && <div className="p-8 text-center text-gray-500">No schedule slots yet</div>}
      </div>
    </div>
  );

  const renderAssessments = ({ hideHeader = false } = {}) => (
    <div className="space-y-4">
      {!hideHeader && (
        <div className="flex justify-end">
          <button onClick={() => openModal('assessment')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
            <Plus className="h-4 w-4" /> Create Assessment
          </button>
        </div>
      )}
      <div className="space-y-2">
        {assessments.map(a => (
          <div key={a.id} className="bg-white p-4 rounded-xl border flex items-center justify-between">
            <div>
              <h4 className="font-medium">{a.name}</h4>
              <p className="text-sm text-gray-500">{a.className} • {a.subjectName} • Total: {a.totalMarks}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-2 py-1 rounded text-xs ${a.status === 'captured' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{a.status}</span>
              <button onClick={() => openModal('assessment', a)} className="p-1 hover:bg-gray-100 rounded"><Edit3 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {assessments.length === 0 && <div className="text-center py-8 text-gray-500">No assessments yet</div>}
      </div>
    </div>
  );

  const renderActivities = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="px-4 py-2 border rounded-lg w-64" />
        <button onClick={() => openModal('activity')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
          <Plus className="h-4 w-4" /> Add Activity
        </button>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        {filter(activities, ['name', 'description']).length === 0 ? (
          <div className="p-8 text-center text-gray-500">No activities found</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Activity</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Due</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filter(activities, ['name', 'description']).map((activity) => {
                const typeMeta = ActivityTypeMeta[activity.activityType] || { label: activity.activityType || 'Activity', color: 'slate' };
                const statusMeta = ActivityStatusMeta[activity.status] || { label: activity.status || 'Pending', color: 'slate' };
                return (
                  <tr key={activity.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">
                      <div>
                        <div className="font-medium text-gray-900">{activity.name}</div>
                        {activity.description && <div className="text-xs text-gray-500 line-clamp-1">{activity.description}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs bg-${typeMeta.color}-100 text-${typeMeta.color}-700`}>
                        {typeMeta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{activity.dueDate || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs bg-${statusMeta.color}-100 text-${statusMeta.color}-700`}>
                        {statusMeta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openModal('activity', activity)} className="p-1 hover:bg-gray-100 rounded"><Edit3 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const renderPlanner = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search plans..." className="px-4 py-2 border rounded-lg w-64" />
          <select value={selectedPlanId} onChange={(e) => setSelectedPlanId(e.target.value)} className="px-3 py-2 border rounded-lg">
            {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => openModal('plan')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add Plan
          </button>
          <button onClick={() => openModal('plan-action', { planId: selectedPlanId })} className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add Action
          </button>
        </div>
      </div>
      <div className="bg-white rounded-xl border overflow-hidden">
        {filter(plans, ['name', 'description', 'planType']).length === 0 ? (
          <div className="p-8 text-center text-gray-500">No plans found</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Plan</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Start</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">End</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filter(plans, ['name', 'description', 'planType']).map((plan) => {
                const typeLabel = PLAN_TYPES.find(t => t.value === plan.planType)?.label || plan.planType || '-';
                return (
                  <tr key={plan.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">
                      <div className="text-gray-900">{plan.name}</div>
                      {plan.description && <div className="text-xs text-gray-500 line-clamp-1">{plan.description}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{typeLabel}</td>
                    <td className="px-4 py-3 text-gray-500">{plan.startDate || '-'}</td>
                    <td className="px-4 py-3 text-gray-500">{plan.endDate || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openModal('plan', plan)} className="p-1 hover:bg-gray-100 rounded"><Edit3 className="h-4 w-4" /></button>
                      <button onClick={() => handleDeletePlan(plan.id)} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
          <div className="font-semibold text-gray-900">Implementation Actions</div>
          {planActions.filter(a => a.startDate && new Date(a.startDate) <= new Date() && !a.activityId).length > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
              {planActions.filter(a => a.startDate && new Date(a.startDate) <= new Date() && !a.activityId).length} ready for graduation
            </span>
          )}
        </div>
        {planActions.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No actions for this plan yet</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Action</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Assignee</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Start</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">End</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {planActions.map(action => {
                const isReady = action.startDate && new Date(action.startDate) <= new Date() && !action.activityId;
                return (
                  <tr key={action.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">
                      <div className="text-gray-900">{action.name}</div>
                      {action.description && <div className="text-xs text-gray-500 line-clamp-1">{action.description}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{action.assigneeName || '-'}</td>
                    <td className="px-4 py-3 text-gray-500">{action.startDate || '-'}</td>
                    <td className="px-4 py-3 text-gray-500">{action.endDate || '-'}</td>
                    <td className="px-4 py-3 text-gray-500">{action.activityId ? 'Graduated' : (isReady ? 'Ready' : 'Planned')}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openModal('plan-action', action)} className="p-1 hover:bg-gray-100 rounded"><Edit3 className="h-4 w-4" /></button>
                      <button onClick={() => handleDeletePlanAction(action.planId, action.id)} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
                      <button
                        onClick={() => handleGraduatePlanAction(action.planId, action)}
                        disabled={!isReady}
                        className={`ml-2 px-2 py-1 rounded text-xs ${isReady ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                      >
                        Graduate
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const renderSystems = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search systems..." className="px-4 py-2 border rounded-lg w-64" />
        <button onClick={() => openModal('system')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
          <Plus className="h-4 w-4" /> Add System
        </button>
      </div>
      <div className="bg-white rounded-xl border overflow-hidden">
        {filter(systems, ['name', 'description', 'systemType']).length === 0 ? (
          <div className="p-8 text-center text-gray-500">No systems found</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">System</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Owner</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filter(systems, ['name', 'description', 'systemType']).map((system) => {
                const typeLabel = SYSTEM_TYPES.find(t => t.value === system.systemType)?.label || system.systemType || '-';
                return (
                  <tr key={system.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">
                      <div className="text-gray-900">{system.name}</div>
                      {system.description && <div className="text-xs text-gray-500 line-clamp-1">{system.description}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{typeLabel}</td>
                    <td className="px-4 py-3 text-gray-500">{system.ownerName || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openModal('system', system)} className="p-1 hover:bg-gray-100 rounded"><Edit3 className="h-4 w-4" /></button>
                      <button onClick={() => handleDeleteSystem(system.id)} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex flex-wrap items-center gap-3 justify-between">
          <div className="font-semibold text-gray-900">Subsystems</div>
          <div className="flex items-center gap-2">
            <select
              value={selectedSystemId}
              onChange={(e) => setSelectedSystemId(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="">Select system</option>
              {systems.map(system => (
                <option key={system.id} value={system.id}>{system.name}</option>
              ))}
            </select>
            <button
              onClick={() => openModal('system-subsystem', { systemId: selectedSystemId })}
              disabled={!selectedSystemId}
              className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
                selectedSystemId ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Plus className="h-4 w-4" /> Add Subsystem
            </button>
          </div>
        </div>
        {selectedSystemId && systemSubsystems.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No subsystems for this system yet</div>
        ) : (
          selectedSystemId && (
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Subsystem</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Owner</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Dates</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {systemSubsystems.map(subsystem => (
                  <tr key={subsystem.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">
                      <div className="text-gray-900">{subsystem.name}</div>
                      {subsystem.description && <div className="text-xs text-gray-500 line-clamp-1">{subsystem.description}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{subsystem.ownerName || '-'}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {subsystem.startDate || '-'} {subsystem.endDate ? `→ ${subsystem.endDate}` : ''}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openModal('system-subsystem', subsystem)} className="p-1 hover:bg-gray-100 rounded"><Edit3 className="h-4 w-4" /></button>
                      <button onClick={() => handleDeleteSystemSubsystem(subsystem.systemId, subsystem.id)} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>
    </div>
  );

  const renderView = () => {
    switch (view) {
      case 'overview': return renderOverview();
      case 'actors': return renderActors();
      case 'items': {
        const tabs = [
          {
            id: 'classes',
            label: 'Classes',
            description: 'Define learning groups and class teachers.',
            actionLabel: 'Add Class',
            action: () => openModal('class'),
            render: () => renderClasses({ hideHeader: true })
          },
          {
            id: 'subjects',
            label: 'Subjects',
            description: 'Set up subject catalog and color coding.',
            actionLabel: 'Add Subject',
            action: () => openModal('subject'),
            render: () => renderSubjects({ hideHeader: true })
          },
          {
            id: 'items',
            label: 'Items',
            description: 'Track school resources, spaces, and equipment.',
            actionLabel: 'Add Item',
            action: () => openModal('item'),
            render: () => renderItems({ hideHeader: true })
          }
        ];
        const activeTab = tabs.find((tab) => tab.id === itemsTab) || tabs[0];
        return (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-2 border-b border-gray-200">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setItemsTab(tab.id)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 ${itemsTab === tab.id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{activeTab.label}</h3>
                <p className="text-sm text-gray-500">{activeTab.description}</p>
              </div>
              <button onClick={activeTab.action} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
                <Plus className="h-4 w-4" /> {activeTab.actionLabel}
              </button>
            </div>
            <div>{activeTab.render()}</div>
          </div>
        );
      }
      case 'activities': return renderActivities();
      case 'timetable': return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Timetable</h3>
              <p className="text-sm text-gray-500">Maintain schedule slots and allocations.</p>
            </div>
            <button onClick={() => openModal('slot')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
              <Plus className="h-4 w-4" /> Add Slot
            </button>
          </div>
          {renderSlots({ hideHeader: true })}
        </div>
      );
      case 'planner': return renderPlanner();
      case 'marklists': return (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border p-6">
            <h3 className="text-lg font-semibold text-gray-900">Marklists</h3>
            <p className="text-sm text-gray-500 mt-2">
              Marklists are derived from assessment score logs. Select an assessment to view captured scores.
            </p>
          </div>
          <div className="space-y-2">
            {assessments.map(a => (
              <div key={a.id} className="bg-white p-4 rounded-xl border flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{a.name}</h4>
                  <p className="text-sm text-gray-500">{a.className} - {a.subjectName} - Scores: {a.scoresCaptured || 0}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${a.status === 'captured' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{a.status}</span>
              </div>
            ))}
            {assessments.length === 0 && <div className="text-center py-8 text-gray-500">No assessments yet</div>}
          </div>
        </div>
      );
      case 'systems': return renderSystems();
      default: return renderOverview();
    }
  };

  // Form Modal
  const FormModal = () => {
    const normalizeForm = (item) => {
      if (modal.type === 'actor') {
        if (!item) return { type: 'learner' };
        const normalizedType = normalizeActorType(item.type);
        return {
          ...item,
          type: normalizedType,
          staffRole: item.staffRole || (item.type === 'teacher' ? 'educator' : ''),
          parentIds: Array.isArray(item.parentIds) ? item.parentIds : [],
          subjectIds: Array.isArray(item.subjectIds) ? item.subjectIds : []
        };
      }
      if (modal.type === 'item') {
        return {
          status: 'available',
          quantity: 1,
          ...item
        };
      }
      if (modal.type === 'system') {
        return {
          ownerId: '',
          ownerName: '',
          createdOn: '',
          updatedOn: '',
          validFrom: '',
          validTo: '',
          utilisation: '',
          ...item
        };
      }
      if (modal.type === 'system-subsystem') {
        return {
          systemId: item?.systemId || selectedSystemId || '',
          ownerId: '',
          ownerName: '',
          status: 'draft',
          startDate: '',
          endDate: '',
          items: [],
          ...item
        };
      }
      return item || {};
    };

    const [form, setForm] = useState(normalizeForm(modal.item));
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [photoError, setPhotoError] = useState('');
    const [localPhotoPreview, setLocalPhotoPreview] = useState('');
    const [uploadingItemImage, setUploadingItemImage] = useState(false);
    const [itemImageError, setItemImageError] = useState('');
    const [localItemImagePreview, setLocalItemImagePreview] = useState('');
    
    const modalItemKey = `${modal.type || 'none'}:${modal.item?.id || 'new'}`;

    useEffect(() => {
      if (!modal.open) return;
      setForm(normalizeForm(modal.item));
      setPhotoError('');
      setLocalPhotoPreview('');
      setItemImageError('');
      setLocalItemImagePreview('');
    }, [modalItemKey, modal.open]);

    useEffect(() => {
      if (!localPhotoPreview) return undefined;
      return () => URL.revokeObjectURL(localPhotoPreview);
    }, [localPhotoPreview]);

    useEffect(() => {
      if (!localItemImagePreview) return undefined;
      return () => URL.revokeObjectURL(localItemImagePreview);
    }, [localItemImagePreview]);
    
    const handleChange = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const stakeholderActors = actors.filter(a => normalizeActorType(a.type) === 'stakeholder');

    const toggleParent = (parent) => {
      setForm(prev => {
        const ids = new Set(prev.parentIds || []);
        if (ids.has(parent.id)) ids.delete(parent.id);
        else ids.add(parent.id);
        const nextIds = Array.from(ids);
        const nextNames = nextIds
          .map(id => stakeholderActors.find(p => p.id === id)?.name)
          .filter(Boolean);
        return { ...prev, parentIds: nextIds, parentNames: nextNames };
      });
    };

    const toggleSubject = (subject) => {
      setForm(prev => {
        const ids = new Set(prev.subjectIds || []);
        if (ids.has(subject.id)) ids.delete(subject.id);
        else ids.add(subject.id);
        const nextIds = Array.from(ids);
        const nextNames = nextIds
          .map(id => subjects.find(s => s.id === id)?.name)
          .filter(Boolean);
        return { ...prev, subjectIds: nextIds, subjectNames: nextNames };
      });
    };

    const handlePhotoUpload = async (file) => {
      if (!file) return;
      setPhotoError('');
      setUploadingPhoto(true);
      try {
        const previewUrl = URL.createObjectURL(file);
        setLocalPhotoPreview(previewUrl);
        const actorId = form.id || 'new';
        const result = await schoolmateService.uploadActorPhoto(bookId, file, actorId);
        handleChange('photoUrl', result.url);
        handleChange('photoPath', result.path);
      } catch (error) {
        setPhotoError(error.message || 'Failed to upload image');
      } finally {
        setUploadingPhoto(false);
      }
    };

    const handleItemImageUpload = async (file) => {
      if (!file) return;
      setItemImageError('');
      setUploadingItemImage(true);
      try {
        const previewUrl = URL.createObjectURL(file);
        setLocalItemImagePreview(previewUrl);
        const itemId = form.id || 'new';
        const result = await schoolmateService.uploadItemImage(bookId, file, itemId);
        handleChange('imageUrl', result.url);
        handleChange('imagePath', result.path);
        handleChange('imageFileName', result.fileName);
        handleChange('imageFileType', result.fileType);
        handleChange('imageFileSize', result.fileSize);
        handleChange('imageUploadedAt', result.uploadedAt);
      } catch (error) {
        setItemImageError(error.message || 'Failed to upload image');
      } finally {
        setUploadingItemImage(false);
      }
    };
    
    const handleSubmit = () => {
      switch (modal.type) {
        case 'actor': {
          const normalizedType = normalizeActorType(form.type);
          const payload = {
            ...form,
            type: normalizedType,
            staffRole: normalizedType === 'staff' ? (form.staffRole || '') : null,
            sgbRole: normalizedType === 'sgb' ? (form.sgbRole || '') : null,
            stakeholderRole: normalizedType === 'stakeholder' ? (form.stakeholderRole || '') : null,
            parentNames: (form.parentIds || [])
              .map(id => stakeholderActors.find(p => p.id === id)?.name)
              .filter(Boolean),
            subjectNames: (form.subjectIds || [])
              .map(id => subjects.find(s => s.id === id)?.name)
              .filter(Boolean)
          };
          handleSaveActor(payload);
          break;
        }
        case 'class': handleSaveClass(form); break;
        case 'subject': handleSaveSubject(form); break;
        case 'item': {
          const normalizedCategory = form.category === '__new__'
            ? (form.newCategory || '').trim()
            : form.category;
          const payload = {
            ...form,
            category: normalizedCategory || '',
            tags: typeof form.tags === 'string'
              ? form.tags.split(',').map(t => t.trim()).filter(Boolean)
              : form.tags
          };
          delete payload.newCategory;
          handleSaveItem(payload);
          break;
        }
        case 'slot': handleSaveSlot(form); break;
        case 'assessment': handleSaveAssessment(form); break;
        case 'task':
          handleSaveTask(form, 'Task');
          break;
        case 'activity':
          handleSaveTask(form, 'Activity');
          break;
        case 'plan':
          handleSavePlan(form);
          break;
        case 'system':
          handleSaveSystem(form);
          break;
        case 'system-subsystem':
          handleSaveSystemSubsystem(form);
          break;
        case 'plan-action':
          handleSavePlanAction(form);
          break;
      }
    };
    
    const renderForm = () => {
      switch (modal.type) {
        case 'actor':
          return (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-indigo-100 overflow-hidden flex items-center justify-center text-indigo-600 font-semibold">
                  {(localPhotoPreview || form.photoUrl) ? (
                    <img src={localPhotoPreview || form.photoUrl} alt={form.name || 'Actor'} className="h-full w-full object-cover" />
                  ) : (
                    (form.name || 'A').slice(0, 1).toUpperCase()
                  )}
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">Profile Image URL</label>
                  <input
                    value={form.photoUrl || ''}
                    onChange={(e) => handleChange('photoUrl', e.target.value)}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <div className="mt-2 flex items-center gap-3">
                    <label className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border cursor-pointer ${uploadingPhoto ? 'opacity-60 pointer-events-none' : 'hover:bg-gray-50'}`}>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                      />
                      {uploadingPhoto ? 'Uploading...' : 'Upload image'}
                    </label>
                    {photoError && <span className="text-xs text-red-500">{photoError}</span>}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select value={form.type || 'learner'} onChange={(e) => handleChange('type', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                  <option value="learner">Learner</option>
                  <option value="staff">Staff</option>
                  <option value="sgb">SGB</option>
                  <option value="stakeholder">Stakeholder</option>
                </select>
              </div>
              {form.type === 'staff' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Staff Role</label>
                  <select value={form.staffRole || ''} onChange={(e) => handleChange('staffRole', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select role</option>
                    {STAFF_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              )}
              {form.type === 'sgb' && (
                <div>
                  <label className="block text-sm font-medium mb-1">SGB Role</label>
                  <select value={form.sgbRole || ''} onChange={(e) => handleChange('sgbRole', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select role</option>
                    {SGB_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              )}
              {form.type === 'stakeholder' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Stakeholder Role</label>
                  <select value={form.stakeholderRole || ''} onChange={(e) => handleChange('stakeholderRole', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select role</option>
                    {STAKEHOLDER_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input value={form.name || ''} onChange={(e) => handleChange('name', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input type="email" value={form.email || ''} onChange={(e) => handleChange('email', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input value={form.phone || ''} onChange={(e) => handleChange('phone', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Alt Phone</label>
                  <input value={form.altPhone || ''} onChange={(e) => handleChange('altPhone', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <input value={form.address || ''} onChange={(e) => handleChange('address', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              {form.type === 'learner' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Class</label>
                  <select value={form.classId || ''} onChange={(e) => { const c = classes.find(x => x.id === e.target.value); handleChange('classId', e.target.value); handleChange('className', c?.name || ''); }} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select class</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              {form.type === 'learner' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Parents / Guardians</label>
                  <div className="border rounded-lg p-2 bg-gray-50 max-h-32 overflow-y-auto space-y-1">
                    {stakeholderActors.length === 0 ? (
                      <p className="text-sm text-gray-500">No stakeholders yet</p>
                    ) : (
                      stakeholderActors.map(parent => {
                        const roleLabel = getRoleLabel(STAKEHOLDER_ROLES, parent.stakeholderRole);
                        return (
                          <label key={parent.id} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={(form.parentIds || []).includes(parent.id)}
                              onChange={() => toggleParent(parent)}
                              className="h-4 w-4"
                            />
                            <span>{parent.name}</span>
                            {roleLabel && <span className="text-xs text-gray-400">({roleLabel})</span>}
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Subjects</label>
                <div className="border rounded-lg p-2 bg-gray-50 max-h-32 overflow-y-auto space-y-1">
                  {subjects.length === 0 ? (
                    <p className="text-sm text-gray-500">No subjects yet</p>
                  ) : (
                    subjects.map(subject => (
                      <label key={subject.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={(form.subjectIds || []).includes(subject.id)}
                          onChange={() => toggleSubject(subject)}
                          className="h-4 w-4"
                        />
                        <span>{subject.name}</span>
                        {subject.code && <span className="text-xs text-gray-400">({subject.code})</span>}
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
          );
        
        case 'class':
          return (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Class Name *</label>
                <input value={form.name || ''} onChange={(e) => handleChange('name', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. 10A" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Grade</label>
                <input value={form.grade || ''} onChange={(e) => handleChange('grade', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. 10" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Class Teacher</label>
                <select value={form.teacherId || ''} onChange={(e) => { const t = actors.find(a => a.id === e.target.value); handleChange('teacherId', e.target.value); handleChange('teacherName', t?.name || ''); }} className="w-full px-3 py-2 border rounded-lg">
                  <option value="">Select teacher</option>
                  {actors
                    .filter(a => normalizeActorType(a.type) === 'staff' && (!a.staffRole || a.staffRole === 'educator'))
                    .map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
          );
        
        case 'subject':
          return (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Subject Name *</label>
                <input value={form.name || ''} onChange={(e) => handleChange('name', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Code</label>
                <input value={form.code || ''} onChange={(e) => handleChange('code', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. MATH" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Color</label>
                <input type="color" value={form.color || '#6366f1'} onChange={(e) => handleChange('color', e.target.value)} className="h-10 w-full rounded-lg" />
              </div>
            </div>
          );

        case 'item': {
          const showNewCategory = form.category === '__new__';
          return (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-xl bg-indigo-100 overflow-hidden flex items-center justify-center text-indigo-600 font-semibold text-xs">
                  {(localItemImagePreview || form.imageUrl) ? (
                    <img src={localItemImagePreview || form.imageUrl} alt={form.name || 'Item'} className="h-full w-full object-cover" />
                  ) : (
                    (form.name || 'I').slice(0, 2).toUpperCase()
                  )}
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">Item Image URL</label>
                  <input
                    value={form.imageUrl || ''}
                    onChange={(e) => handleChange('imageUrl', e.target.value)}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <div className="mt-2 flex items-center gap-3">
                    <label className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border cursor-pointer ${uploadingItemImage ? 'opacity-60 pointer-events-none' : 'hover:bg-gray-50'}`}>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleItemImageUpload(e.target.files?.[0])}
                      />
                      {uploadingItemImage ? 'Uploading...' : 'Upload image'}
                    </label>
                    {itemImageError && <span className="text-xs text-red-500">{itemImageError}</span>}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Item Name *</label>
                <input value={form.name || ''} onChange={(e) => handleChange('name', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Projector, Lab 1, Sports Field" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <select value={form.itemType || ''} onChange={(e) => handleChange('itemType', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select type</option>
                    {ITEM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Ownership</label>
                  <select value={form.ownership || ''} onChange={(e) => handleChange('ownership', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select ownership</option>
                    {ITEM_OWNERSHIPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select value={form.category || ''} onChange={(e) => handleChange('category', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select category</option>
                    {itemCategories.map(c => <option key={c} value={c}>{c}</option>)}
                    <option value="__new__">+ Add new category</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <select value={form.status || 'available'} onChange={(e) => handleChange('status', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                    <option value="available">Available</option>
                    <option value="in-use">In Use</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="missing">Missing</option>
                    <option value="retired">Retired</option>
                  </select>
                </div>
              </div>
              {showNewCategory && (
                <div>
                  <label className="block text-sm font-medium mb-1">New Category</label>
                  <input value={form.newCategory || ''} onChange={(e) => handleChange('newCategory', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Vehicles, Library Resource" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Location</label>
                  <input value={form.location || ''} onChange={(e) => handleChange('location', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Block B, Store Room" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Quantity</label>
                  <input type="number" min="1" value={form.quantity || 1} onChange={(e) => handleChange('quantity', parseInt(e.target.value || '1', 10))} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Assigned To</label>
                <select value={form.assignedToId || ''} onChange={(e) => { const a = actors.find(x => x.id === e.target.value); handleChange('assignedToId', e.target.value); handleChange('assignedToName', a?.name || ''); }} className="w-full px-3 py-2 border rounded-lg">
                  <option value="">Unassigned</option>
                  {actors.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Serial Number</label>
                <input value={form.serialNumber || ''} onChange={(e) => handleChange('serialNumber', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Condition</label>
                <input value={form.condition || ''} onChange={(e) => handleChange('condition', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Good, Needs repair" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tags (comma separated)</label>
                <input value={form.tags || ''} onChange={(e) => handleChange('tags', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. projector, grade-10" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea value={form.description || ''} onChange={(e) => handleChange('description', e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows={3} />
              </div>
            </div>
          );
        }
        
        case 'slot':
          return (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Day *</label>
                  <select value={form.day || 'monday'} onChange={(e) => handleChange('day', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                    {DAYS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Period *</label>
                  <input type="number" min="1" value={form.period || 1} onChange={(e) => handleChange('period', parseInt(e.target.value))} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Time</label>
                  <input type="time" value={form.startTime || '08:00'} onChange={(e) => handleChange('startTime', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End Time</label>
                  <input type="time" value={form.endTime || '08:45'} onChange={(e) => handleChange('endTime', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Class *</label>
                <select value={form.classId || ''} onChange={(e) => { const c = classes.find(x => x.id === e.target.value); handleChange('classId', e.target.value); handleChange('className', c?.name || ''); }} className="w-full px-3 py-2 border rounded-lg">
                  <option value="">Select class</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Subject *</label>
                <select value={form.subjectId || ''} onChange={(e) => { const s = subjects.find(x => x.id === e.target.value); handleChange('subjectId', e.target.value); handleChange('subjectName', s?.name || ''); }} className="w-full px-3 py-2 border rounded-lg">
                  <option value="">Select subject</option>
                  {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Teacher</label>
                <select value={form.teacherId || ''} onChange={(e) => { const t = actors.find(a => a.id === e.target.value); handleChange('teacherId', e.target.value); handleChange('teacherName', t?.name || ''); }} className="w-full px-3 py-2 border rounded-lg">
                  <option value="">Select teacher</option>
                  {actors
                    .filter(a => normalizeActorType(a.type) === 'staff' && (!a.staffRole || a.staffRole === 'educator'))
                    .map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
          );
        
        case 'assessment':
          return (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input value={form.name || ''} onChange={(e) => handleChange('name', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Term 1 Test" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Class</label>
                  <select value={form.classId || ''} onChange={(e) => { const c = classes.find(x => x.id === e.target.value); handleChange('classId', e.target.value); handleChange('className', c?.name || ''); }} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select class</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Subject</label>
                  <select value={form.subjectId || ''} onChange={(e) => { const s = subjects.find(x => x.id === e.target.value); handleChange('subjectId', e.target.value); handleChange('subjectName', s?.name || ''); }} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select subject</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Total Marks</label>
                  <input type="number" value={form.totalMarks || 100} onChange={(e) => handleChange('totalMarks', parseInt(e.target.value))} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Due Date</label>
                  <input type="date" value={form.dueDate || ''} onChange={(e) => handleChange('dueDate', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
            </div>
          );
        
        case 'task':
        case 'activity': {
          const activityType = form.activityType || ActivityType.TASK;
          const referenceType = form.referenceType || 'actor';
          const references = Array.isArray(form.references) ? form.references : [];
          const referenceOptions = referenceType === 'actor'
            ? actors
            : referenceType === 'item'
              ? items
              : activities;
          const isReferenceChecked = (id) => references.some((ref) => ref.type === referenceType && ref.id === id);
          const toggleReference = (entity) => {
            if (!entity?.id) return;
            const exists = references.some((ref) => ref.type === referenceType && ref.id === entity.id);
            const next = exists
              ? references.filter((ref) => !(ref.type === referenceType && ref.id === entity.id))
              : [...references, { type: referenceType, id: entity.id, name: entity.name || entity.title || '' }];
            handleChange('references', next);
          };
          return (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Activity Type</label>
                <select value={activityType} onChange={(e) => handleChange('activityType', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                  {ACTIVITY_TYPE_OPTIONS.map(option => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </select>
                {ActivityTypeMeta[activityType]?.description && (
                  <p className="text-xs text-gray-500 mt-1">{ActivityTypeMeta[activityType].description}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Activity Name *</label>
                <input value={form.name || ''} onChange={(e) => handleChange('name', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea value={form.description || ''} onChange={(e) => handleChange('description', e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows={3} />
              </div>

              {(activityType === ActivityType.TASK || activityType === ActivityType.TODO || activityType === ActivityType.PROJECT) && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Due Date</label>
                      <input type="date" value={form.dueDate || ''} onChange={(e) => handleChange('dueDate', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Priority</label>
                      <select value={form.priority || 'medium'} onChange={(e) => handleChange('priority', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                        <option value="urgent">Urgent</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Plan ID (optional)</label>
                    <input value={form.planId || ''} onChange={(e) => handleChange('planId', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="Link to operational plan" />
                  </div>
                </div>
              )}

              {(activityType === ActivityType.MEETING || activityType === ActivityType.EVENT) && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Start Time</label>
                      <input type="datetime-local" value={form.startTime || ''} onChange={(e) => handleChange('startTime', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Duration (mins)</label>
                      <input type="number" min="0" value={form.durationMins || ''} onChange={(e) => handleChange('durationMins', e.target.value ? parseInt(e.target.value, 10) : '')} className="w-full px-3 py-2 border rounded-lg" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Mode</label>
                    <select value={form.mode || 'physical'} onChange={(e) => handleChange('mode', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                      <option value="physical">Physical</option>
                      <option value="virtual">Virtual</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </div>
                  {(form.mode || 'physical') !== 'virtual' && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Location</label>
                      <input value={form.location || ''} onChange={(e) => handleChange('location', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="Room, venue, address" />
                    </div>
                  )}
                  {(form.mode || 'physical') !== 'physical' && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Meeting Link</label>
                      <input value={form.meetingLink || ''} onChange={(e) => handleChange('meetingLink', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="https://..." />
                    </div>
                  )}
                  {activityType === ActivityType.MEETING && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Agenda Items</label>
                      <textarea value={form.agenda || ''} onChange={(e) => handleChange('agenda', e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows={3} placeholder="Bullet points or agenda items..." />
                    </div>
                  )}
                </div>
              )}

              {(activityType === ActivityType.NOTE || activityType === ActivityType.REFLECTION || activityType === ActivityType.RECORD || activityType === ActivityType.DECISION || activityType === ActivityType.LOG) && (
                <div>
                  <label className="block text-sm font-medium mb-1">Entry Details</label>
                  <textarea value={form.entry || ''} onChange={(e) => handleChange('entry', e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows={4} placeholder="Write the details here..." />
                </div>
              )}

              {activityType === ActivityType.DECISION && (
                <div>
                  <label className="block text-sm font-medium mb-1">Rationale</label>
                  <textarea value={form.rationale || ''} onChange={(e) => handleChange('rationale', e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows={3} placeholder="Why this decision was made..." />
                </div>
              )}

              <div className="border-t pt-4">
                <label className="block text-sm font-medium mb-2">References</label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Entity Type</label>
                    <select value={referenceType} onChange={(e) => handleChange('referenceType', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                      <option value="actor">Actors</option>
                      <option value="item">Items</option>
                      <option value="activity">Activities</option>
                    </select>
                  </div>
                </div>
                <div className="mt-3 max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1 bg-gray-50">
                  {referenceOptions.length === 0 ? (
                    <p className="text-sm text-gray-500">No entities available.</p>
                  ) : (
                    referenceOptions.map((entity) => (
                      <label key={entity.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={isReferenceChecked(entity.id)}
                          onChange={() => toggleReference(entity)}
                        />
                        <span>{entity.name || entity.title || entity.id}</span>
                      </label>
                    ))
                  )}
                </div>
                {references.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {references.map((ref) => (
                      <span key={`${ref.type}-${ref.id}`} className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                        {ref.name || ref.id} • {ref.type}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        }

        case 'plan':
          return (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Plan Name *</label>
                <input value={form.name || ''} onChange={(e) => handleChange('name', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Plan Type</label>
                <select value={form.planType || 'improvement'} onChange={(e) => handleChange('planType', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                  {PLAN_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Date</label>
                  <input type="date" value={form.startDate || ''} onChange={(e) => handleChange('startDate', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End Date</label>
                  <input type="date" value={form.endDate || ''} onChange={(e) => handleChange('endDate', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Owner</label>
                <select
                  value={form.ownerId || ''}
                  onChange={(e) => {
                    const actor = actors.find(a => a.id === e.target.value);
                    handleChange('ownerId', e.target.value);
                    handleChange('ownerName', actor?.name || '');
                  }}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select owner</option>
                  {actors.map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea value={form.description || ''} onChange={(e) => handleChange('description', e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows={3} />
              </div>
            </div>
          );

        case 'system':
          return (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">System Name *</label>
                <input value={form.name || ''} onChange={(e) => handleChange('name', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">System Type</label>
                <select value={form.systemType || 'vision'} onChange={(e) => handleChange('systemType', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                  {SYSTEM_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Owner</label>
                <select
                  value={form.ownerId || ''}
                  onChange={(e) => {
                    const owner = actors.find(a => a.id === e.target.value);
                    handleChange('ownerId', e.target.value);
                    handleChange('ownerName', owner?.name || '');
                  }}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select owner</option>
                  {actors.map(actor => (
                    <option key={actor.id} value={actor.id}>{actor.name}</option>
                  ))}
                </select>
                {form.ownerName && (
                  <p className="text-xs text-gray-500 mt-1">Owner: {form.ownerName}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Creation Date</label>
                  <input type="date" value={form.createdOn || ''} onChange={(e) => handleChange('createdOn', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Update Date</label>
                  <input type="date" value={form.updatedOn || ''} onChange={(e) => handleChange('updatedOn', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Valid From</label>
                  <input type="date" value={form.validFrom || ''} onChange={(e) => handleChange('validFrom', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Valid To</label>
                  <input type="date" value={form.validTo || ''} onChange={(e) => handleChange('validTo', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea value={form.description || ''} onChange={(e) => handleChange('description', e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows={3} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Envisaged Utilisation</label>
                <textarea value={form.utilisation || ''} onChange={(e) => handleChange('utilisation', e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows={2} placeholder="How will this system be used?" />
              </div>
            </div>
          );

        case 'system-subsystem': {
          const subsystemSystemId = form.systemId || selectedSystemId || '';
          const selectedSystem = systems.find(s => s.id === subsystemSystemId);
          const subsystemItems = Array.isArray(form.items) ? form.items : [];
          const updateItem = (idx, key, value) => {
            const next = subsystemItems.map((item, i) => (i === idx ? { ...item, [key]: value } : item));
            handleChange('items', next);
          };
          const addItem = () => handleChange('items', [...subsystemItems, { name: '', description: '' }]);
          const removeItem = (idx) => handleChange('items', subsystemItems.filter((_, i) => i !== idx));
          return (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">System *</label>
                <select
                  value={subsystemSystemId}
                  onChange={(e) => handleChange('systemId', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select system</option>
                  {systems.map(system => (
                    <option key={system.id} value={system.id}>{system.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Subsystem Name *</label>
                <input value={form.name || ''} onChange={(e) => handleChange('name', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea value={form.description || ''} onChange={(e) => handleChange('description', e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows={3} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Owner</label>
                <select
                  value={form.ownerId || ''}
                  onChange={(e) => {
                    const owner = actors.find(a => a.id === e.target.value);
                    handleChange('ownerId', e.target.value);
                    handleChange('ownerName', owner?.name || '');
                  }}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select owner</option>
                  {actors.map(actor => (
                    <option key={actor.id} value={actor.id}>{actor.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Date</label>
                  <input type="date" value={form.startDate || ''} onChange={(e) => handleChange('startDate', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End Date</label>
                  <input type="date" value={form.endDate || ''} onChange={(e) => handleChange('endDate', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select value={form.status || 'draft'} onChange={(e) => handleChange('status', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="retired">Retired</option>
                </select>
              </div>
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium">Subsystem Items</label>
                  <button onClick={addItem} type="button" className="px-3 py-1.5 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">Add Item</button>
                </div>
                {subsystemItems.length === 0 ? (
                  <p className="text-sm text-gray-500">No items yet.</p>
                ) : (
                  subsystemItems.map((item, idx) => (
                    <div key={`subsystem-item-${idx}`} className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Item {idx + 1}</span>
                        <button onClick={() => removeItem(idx)} type="button" className="text-xs text-red-600 hover:underline">Remove</button>
                      </div>
                      <input
                        value={item.name || ''}
                        onChange={(e) => updateItem(idx, 'name', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        placeholder="Item name"
                      />
                      <textarea
                        value={item.description || ''}
                        onChange={(e) => updateItem(idx, 'description', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        rows={2}
                        placeholder="Item description"
                      />
                    </div>
                  ))
                )}
              </div>
              {selectedSystem && (
                <p className="text-xs text-gray-500">System: {selectedSystem.name}</p>
              )}
            </div>
          );
        }

        case 'plan-action': {
          const actionPlanId = form.planId || selectedPlanId || '';
          const selectedPlan = plans.find(p => p.id === actionPlanId);
          const assigneeId = form.assigneeId || '';
          const assignedActor = actors.find(a => a.id === assigneeId);
          const resources = Array.isArray(form.resources) ? form.resources : [];
          const resourceOptions = items || [];
          const isResourceChecked = (id) => resources.some((res) => res.id === id);
          const toggleResource = (item) => {
            if (!item?.id) return;
            const exists = resources.some((res) => res.id === item.id);
            const next = exists
              ? resources.filter((res) => res.id !== item.id)
              : [...resources, { id: item.id, name: item.name || '' }];
            handleChange('resources', next);
          };
          return (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Plan *</label>
                <select value={actionPlanId} onChange={(e) => { handleChange('planId', e.target.value); const plan = plans.find(p => p.id === e.target.value); handleChange('planName', plan?.name || ''); }} className="w-full px-3 py-2 border rounded-lg">
                  <option value="">Select plan</option>
                  {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Action Name *</label>
                <input value={form.name || ''} onChange={(e) => handleChange('name', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea value={form.description || ''} onChange={(e) => handleChange('description', e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows={3} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Targeted Outcomes</label>
                <textarea value={form.outcomes || ''} onChange={(e) => handleChange('outcomes', e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Date *</label>
                  <input type="date" value={form.startDate || ''} onChange={(e) => handleChange('startDate', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End Date</label>
                  <input type="date" value={form.endDate || ''} onChange={(e) => handleChange('endDate', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Assignee</label>
                <select value={assigneeId} onChange={(e) => { const actor = actors.find(a => a.id === e.target.value); handleChange('assigneeId', e.target.value); handleChange('assigneeName', actor?.name || ''); }} className="w-full px-3 py-2 border rounded-lg">
                  <option value="">Select assignee</option>
                  {actors.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {assignedActor && (
                  <p className="text-xs text-gray-500 mt-1">{assignedActor.email || assignedActor.position || ''}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Indicators</label>
                <input value={form.indicators || ''} onChange={(e) => handleChange('indicators', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. improved pass rate, attendance %" />
              </div>
              <div className="border-t pt-4">
                <label className="block text-sm font-medium mb-2">Resources</label>
                <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1 bg-gray-50">
                  {resourceOptions.length === 0 ? (
                    <p className="text-sm text-gray-500">No items available.</p>
                  ) : (
                    resourceOptions.map((item) => (
                      <label key={item.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={isResourceChecked(item.id)}
                          onChange={() => toggleResource(item)}
                        />
                        <span>{item.name || item.id}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              {selectedPlan && (
                <p className="text-xs text-gray-500">Plan: {selectedPlan.name}</p>
              )}
            </div>
          );
        }
        
        default:
          return null;
      }
    };
    
    const titles = { actor: 'Person', class: 'Class', subject: 'Subject', item: 'Item', slot: 'Schedule Slot', assessment: 'Assessment', task: 'Task', activity: 'Activity', plan: 'Plan', system: 'System', 'plan-action': 'Action', 'system-subsystem': 'Subsystem' };
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold">{form.id ? 'Edit' : 'Add'} {titles[modal.type]}</h3>
            <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5" /></button>
          </div>
          <div className="p-4 overflow-y-auto">{renderForm()}</div>
          <div className="p-4 border-t flex justify-end gap-2">
            <button onClick={closeModal} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleSubmit} disabled={saving || uploadingPhoto} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
              {(saving || uploadingPhoto) && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl p-8 flex items-center gap-4 shadow-lg">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b">
        <div className="max-w-6xl mx-auto w-full px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-xl"><School className="h-6 w-6 text-indigo-600" /></div>
            <div>
              <h2 className="font-bold text-lg">School Manager</h2>
              <p className="text-sm text-gray-500">Manage your school data</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-6 w-6" /></button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex flex-1 w-full max-w-6xl mx-auto">
        {/* Sidebar */}
        <div className="w-52 border-r bg-white p-2 overflow-y-auto">
          {NAV_ITEMS.map(n => (
            <button key={n.id} onClick={() => setView(n.id)} className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium mb-1 ${view === n.id ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:bg-gray-100'}`}>
              <n.icon className="h-4 w-4" />{n.label}
            </button>
          ))}
        </div>
        
        {/* Main */}
        <div className="flex-1 p-6 overflow-y-auto">
          {renderView()}
        </div>
      </div>
      
      {/* Notification */}
      {notification && (
        <div className={`fixed bottom-4 right-4 px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 ${notification.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>
          {notification.type === 'error' ? <AlertCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
          {notification.msg}
        </div>
      )}
      
      {/* Modal */}
      {modal.open && <FormModal />}
    </div>
  );

};

export default SchoolmateManager;
